import {
  getProductDetails,
  getWishlistByShareHash,
  getWishlists,
  removeFromWishlist,
} from "@app/scripts/api/wishlist"
import {
  IWishlist,
  IWishlistItem,
  IWishlistItemImage,
} from "@app/types/wishlist"
import { Media, Product, Variant } from "@app/types"
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import ReactDOM from "react-dom/client"
import { CardProduct } from "@app/scripts/components/card-product"
import { chevronDown } from "@app/scripts/hooks/icons"
import { selectDefaultWishlist } from "@app/scripts/utils"
import { currentCountryCurrency } from "@app/scripts/lib/utils"
import {
  useShopifyPrice,
  mergeResultsWithPricing,
} from "@app/scripts/hooks/use-shopify"
export const WishlistProductGrid = () => {
  const [wishlist, setWishlist] = useState<IWishlist | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isShared, setIsShared] = useState(false)

  const fetchWishlist = async (useCache = true) => {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has("share_id")) {
      const wishlist = await getWishlistByShareHash(
        encodeURIComponent(urlParams.get("share_id") || ""),
        true
      )
      if (wishlist) setWishlist(wishlist)
      setIsShared(true)
      return
    }

    const owner =
      window.ShopifyAnalytics.meta.page.customerId?.toString() || null
    if (!owner) return

    const wishlists = await getWishlists(owner, useCache)
    const wishlist = selectDefaultWishlist(wishlists?.wishlists || [])
    setWishlist(wishlist)
  }

  useEffect(() => {
    fetchWishlist()
  }, [])

  const products = useMemo(
    () =>
      wishlist?.items.item?.map((item) => mapWishlistItemToProduct(item)) || [],
    [wishlist]
  )

  const handleWishlistClick = useCallback(
    async (wishlistItemId?: string) => {
      if (isShared) return

      const wishlistItem = wishlist?.items?.item?.find((item) => {
        return item.id === wishlistItemId
      })

      if (!wishlist || !wishlistItemId || !wishlistItem || isLoading) return

      setIsLoading(true)
      await removeFromWishlist(
        wishlistItem.id,
        String(wishlistItem.product.id),
        String(wishlistItem.product.variants[0].id),
        wishlist.id
      )

      window.location.reload()
    },
    [wishlist]
  )

  // Formats data for useShopifyPrice
  const formattedProductData = useMemo(
    () =>
      products.map((result) => ({
        price: result.price || 0,
        compareAtPrice: result.compare_at_price || 0,
        id: result?.variants[0]?.id || "",
        handle: result.handle || "",
      })),
    [products]
  )

  // Formats data (returns new prices or AUD prices)
  const formattedPriceData = useShopifyPrice(
    formattedProductData,
    currentCountryCurrency
  )

  // Create new products array with updated price data or defaults back to results
  const productsWithUpdatedPrices = useMemo(() => {
    return mergeResultsWithPricing(products, formattedPriceData)
  }, [products, formattedPriceData])

  if (!wishlist) return null

  return (
    <div className="wishlist-products__main__product-cards">
      {productsWithUpdatedPrices.map((product, index) => {
        const wishlistItem = wishlist?.items?.item?.[index]
        if (!wishlistItem) return null
        return (
          <CardProduct
            key={wishlistItem.id}
            data={product}
            currentCountryCurrency={currentCountryCurrency}
            source="shopify"
            productCarousel={true}
            wishlistItemId={isShared ? undefined : wishlistItem.id}
            displayWishlistButton={!isShared}
            isWishlistProductGrid={true}
            onWishlistClick={isShared ? undefined : handleWishlistClick}
            extraBottomContent={
              <div className="wishlist-products__main__product-cards--bottom-content">
                <AddToCardButton
                  variantId={String(wishlistItem.product.variants[0].id)}
                  productHandle={wishlistItem.product.handle}
                />
              </div>
            }
          />
        )
      })}
    </div>
  )
}

class WishlistProductGridElement extends HTMLElement {
  connectedCallback() {
    const root = ReactDOM.createRoot(this)
    root.render(<WishlistProductGrid />)
  }
}

customElements.define("wishlist-product-grid", WishlistProductGridElement)

const mapWishlistItemToProduct = (item: IWishlistItem): Product => {
  const wishlistItemProduct = item.product
  const firstVariant = wishlistItemProduct.variants?.[0]

  const prices =
    wishlistItemProduct.variants?.map((v) => parseFloat(v.price)) || []
  const compareAtPrices =
    wishlistItemProduct.variants
      ?.map((v) => v.compare_at_price)
      .filter((p): p is number => p != null && p > 0) || []

  const priceMin = prices.length > 0 ? Math.min(...prices) : 0
  const priceMax = prices.length > 0 ? Math.max(...prices) : 0

  const compareAtPriceMin =
    compareAtPrices.length > 0 ? Math.min(...compareAtPrices) : 0
  const compareAtPriceMax =
    compareAtPrices.length > 0 ? Math.max(...compareAtPrices) : 0

  const mappedVariants: Variant[] =
    wishlistItemProduct.variants?.map((v) => {
      const variantOptions = v.title.split(/\s*\/\s*/)
      return {
        id: v.id,
        title: v.title,
        option1: variantOptions[0] || null,
        option2: variantOptions[1] || null,
        option3: variantOptions[2] || null,
        sku: v.sku?.toString() || null,
        requires_shipping: true, // Default
        taxable: true, // Default
        featured_image: wishlistItemProduct.image?.src || null, // Use main product image or null
        available: v.inventory_quantity > 0,
        name: v.title,
        public_title: v.title,
        options: variantOptions,
        price: parseFloat(v.price),
        weight: 0, // Default
        compare_at_price: v.compare_at_price || null,
        inventory_management: "", // Default
        barcode: null, // Default
        requires_selling_plan: false, // Default
        selling_plan_allocations: [], // Default
      }
    }) || []

  const mappedMedia: Media[] =
    wishlistItemProduct.images?.map((img: IWishlistItemImage) => ({
      alt: null, // Not available in IWishlistItemImage
      id: img.id,
      position: img.position,
      preview_image: {
        aspect_ratio: img.width && img.height ? img.width / img.height : 0,
        height: img.height,
        width: img.width,
        src: img.src,
      },
      aspect_ratio: img.width && img.height ? img.width / img.height : 0,
      height: img.height,
      media_type: "image", // Default
      src: img.src,
      width: img.width,
    })) || []

  const productOptions =
    wishlistItemProduct.options?.option?.map((opt) => ({
      name: opt.name,
      values: opt.values,
    })) || []

  return {
    id: wishlistItemProduct.id,
    title: wishlistItemProduct.title,
    name: wishlistItemProduct.title,
    handle: wishlistItemProduct.handle,
    url: "/products/" + wishlistItemProduct.handle,
    description: "", // Not available
    published_at: "", // Not available
    created_at: "", // Not available
    vendor: wishlistItemProduct.vendor,
    type: "", // Not available
    tags: [], // Not available
    price: firstVariant ? parseFloat(firstVariant.price) : 0,
    price_min: priceMin,
    price_max: priceMax,
    available:
      wishlistItemProduct.variants?.some((v) => v.inventory_quantity > 0) ||
      false,
    price_varies: priceMin !== priceMax,
    compare_at_price: firstVariant?.compare_at_price || null,
    compare_at_price_min: compareAtPriceMin,
    compare_at_price_max: compareAtPriceMax,
    compare_at_price_varies:
      compareAtPrices.length > 0 && compareAtPriceMin !== compareAtPriceMax,
    // variant_options: productOptions,
    variants: mappedVariants,
    images: wishlistItemProduct.images?.map((img) => img.src) || [],
    featured_image: wishlistItemProduct.image?.src || "",
    options: productOptions,
    media: mappedMedia,
    requires_selling_plan: false, // Default
    selling_plan_groups: [], // Default
    content: "", // Not available
  }
}

const AddToCardButton = ({
  variantId,
  productHandle,
}: {
  variantId: string
  productHandle: string
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [productData, setProductData] = useState<{
    variants: {
      id: number
      option2: string
      barcode: string
      available: boolean
    }[]
  } | null>(null)
  const [sizeOptions, setSizeOptions] = useState<string[]>([])
  const [selectedVariantId, setSelectedVariantId] = useState(variantId)

  const selectedVariant = useMemo(
    () => productData?.variants.find((v) => String(v.id) === selectedVariantId),
    [productData?.variants, selectedVariantId]
  )

  useEffect(() => {
    getProductDetails(productHandle).then((product) => {
      setProductData(product)
      setSizeOptions(
        product.options?.find(
          (option: { name: string }) => option.name === "Size"
        )?.values || []
      )
    })
  }, [])

  const handleClick = useCallback(async () => {
    setIsLoading(true)
    const { barcode } = selectedVariant ?? {}

    await addToCart({ variantId: selectedVariantId, barcode })
    setIsLoading(false)
  }, [selectedVariant])

  const handleSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const size = e.target.value
    const variantId = productData?.variants.find(
      (variant: { option2: string }) => variant.option2 === size
    )?.id
    if (variantId) setSelectedVariantId(String(variantId))
  }

  return (
    <>
      <div className="select">
        <select className="full" onChange={handleSizeChange}>
          {sizeOptions.map((sizeOption) => (
            <option value={sizeOption}>{sizeOption}</option>
          ))}
        </select>
        <div className="select-arrow">{chevronDown}</div>
      </div>

      <button
        type="submit"
        name="add"
        className="button"
        onClick={handleClick}
        disabled={isLoading || !selectedVariant?.available}
      >
        {selectedVariant?.available ? "Add to Cart" : "Sold Out"}
      </button>
    </>
  )
}

const addToCart = async ({
  variantId,
  barcode,
}: {
  variantId: string
  barcode?: string
}) => {
  const formData = {
    items: [
      {
        id: Number(variantId),
        quantity: 1,
        ...(barcode
          ? {
              properties: {
                _barcode: barcode,
              },
            }
          : {}),
      },
    ],
  }

  await fetch(window.Shopify.routes.root + "cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(formData),
  })

  document.documentElement.dispatchEvent(
    new CustomEvent("cart:refresh", {
      bubbles: true,
    })
  )
  setTimeout(() => document.getElementById("cart-drawer-toggle")?.click(), 1000)
}
